import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
  IconButton,
  Button,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import FilterListIcon from "@material-ui/icons/FilterList";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import SchoolIcon from "@material-ui/icons/School";
import ClassIcon from "@material-ui/icons/Class";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory, useLocation } from "react-router-dom";
import { getDashboardPageStyles } from "../../utils/dashboardPageStyles";
import {
  ACTIVITY_CATALOG_TABS,
  getUserDetails,
  isSuperAdminRole,
  isSchooleRole,
  normalizeId,
  resolveClassLabel,
  resolveSchoolId,
  resolveSchoolLabel,
  tabIndexFromQuery,
} from "../../utils/activityCatalogScope";
import AddClassService from "./Locality/Service/addClassService";
import SchoolService from "./Locality/Service/schoolService";
import SchooleRegistrationService from "./Locality/Service/schooleRegistration";
import SuperActivity from "./SuperActivity";
import ActivityTabel from "./ActivityTabel";
import SubActivity from "./SubActivity";

const useStyles = makeStyles((theme) => ({
  ...getDashboardPageStyles(theme),
  tabsCard: {
    borderRadius: 14,
    border: "1px solid #e6eaf0",
    boxShadow: "0 2px 10px rgba(16,42,67,0.06)",
    marginBottom: theme.spacing(2),
    overflow: "hidden",
    backgroundColor: "#fff",
  },
  tabsRoot: {
    borderBottom: "1px solid #eef1f5",
    backgroundColor: "#fafbfc",
    minHeight: 48,
  },
  tabPanel: {
    paddingTop: theme.spacing(2),
  },
}));

export default function ActivityCatalog() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const userDetails = useMemo(() => getUserDetails(), []);
  const isSuperAdmin = isSuperAdminRole(userDetails);
  const isSchoole = isSchooleRole(userDetails);
  const canAccess = isSuperAdmin;
  const lockedSchoolId = isSchoole ? resolveSchoolId(userDetails) : "";

  const [tab, setTab] = useState(() => tabIndexFromQuery(location.search));
  const [filterSchoolId, setFilterSchoolId] = useState(lockedSchoolId);
  const [filterClassId, setFilterClassId] = useState("");
  const [schoolList, setSchoolList] = useState([]);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [classList, setClassList] = useState([]);
  const [noteOpen, setNoteOpen] = useState(true);

  useEffect(() => {
    setTab(tabIndexFromQuery(location.search));
  }, [location.search]);

  useEffect(() => {
    if (lockedSchoolId) {
      setFilterSchoolId(lockedSchoolId);
    }
  }, [lockedSchoolId]);

  const handleTabChange = (_, nextTab) => {
    setTab(nextTab);
    const tabKey = ACTIVITY_CATALOG_TABS[nextTab]?.key || "areas";
    history.replace(`/app/activity-catalog?tab=${tabKey}`);
  };

  const loadSchools = useCallback(async () => {
    if (isSchoole && lockedSchoolId) {
      try {
        const school = await SchooleRegistrationService.getSchoolById(lockedSchoolId);
        setSchoolList(school ? [school] : []);
      } catch {
        setSchoolList([]);
      }
      return;
    }

    if (!isSuperAdmin) return;

    setSchoolsLoading(true);
    try {
      let rows = await SchoolService.getAllSchoolList();
      rows = Array.isArray(rows) ? rows : [];
      if (!rows.length) {
        const fallback = await SchooleRegistrationService.getAllSchooleRegistration().catch(() => []);
        rows = Array.isArray(fallback) ? fallback : [];
      }
      setSchoolList(rows);
    } catch {
      setSchoolList([]);
    } finally {
      setSchoolsLoading(false);
    }
  }, [isSuperAdmin, isSchoole, lockedSchoolId]);

  const loadClasses = useCallback(() => {
    const listId = filterSchoolId || (isSuperAdmin ? "all" : lockedSchoolId);
    if (!listId) {
      setClassList([]);
      return;
    }
    const role = isSuperAdmin ? "SUPERADMIN" : isSchoole ? "SCHOOLE" : undefined;
    AddClassService.getAllAddClass(listId, role)
      .then((res) => {
        const rows = Array.isArray(res) ? res : [];
        setClassList(rows);
        if (filterClassId && !rows.some((c) => String(c._id) === String(filterClassId))) {
          setFilterClassId("");
        }
      })
      .catch(() => setClassList([]));
  }, [filterSchoolId, filterClassId, isSuperAdmin, isSchoole, lockedSchoolId]);

  useEffect(() => {
    loadSchools();
  }, [loadSchools]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  const scope = useMemo(
    () => ({
      filterSchoolId,
      filterClassId,
      userDetails,
      isSuperAdmin,
    }),
    [filterSchoolId, filterClassId, userDetails, isSuperAdmin]
  );

  const showSchoolOnClassOptions = isSuperAdmin && !filterSchoolId;

  const handleResetFilters = () => {
    setFilterSchoolId(lockedSchoolId || "");
    setFilterClassId("");
  };

  if (!canAccess) {
    return (
      <Box className={classes.pageWrapper}>
        <Typography className={classes.pageTitle}>Activity Catalog</Typography>
        <Typography className={classes.pageSubtitle} style={{ marginTop: 8 }}>
          Activity Catalog is available for Super Admin only. Use the Activity menu for your school.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.pageWrapper}>
      <Box className={classes.pageHeader}>
        <Box>
          <Typography className={classes.pageTitle}>Activity Catalog</Typography>
          <Typography className={classes.pageSubtitle}>
            {isSuperAdmin
              ? "Manage area of work, list of activities, and exercises across all schools from one place."
              : "Manage area of work, list of activities, and exercises for your school."}
          </Typography>
        </Box>
      </Box>

      {noteOpen && (
        <Box className={classes.noteBanner}>
          <Box className={classes.noteIconWrap}>
            <InfoOutlinedIcon style={{ fontSize: 18 }} />
          </Box>
          <Box flex={1}>
            <Typography style={{ fontWeight: 600, color: "#1f3a5f", fontSize: "0.9rem" }}>
              {isSuperAdmin ? "Default catalog" : "School catalog"}
            </Typography>
            <Typography style={{ color: "#46566b", fontSize: "0.85rem", marginTop: 2 }}>
              {isSuperAdmin
                ? "By default all schools are shown. Use the School filter to narrow data, then optionally pick a Class."
                : "Your school is selected automatically. Pick a class to narrow the catalog further."}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setNoteOpen(false)}>
            <CloseIcon style={{ fontSize: 18, color: "#7a8aa0" }} />
          </IconButton>
        </Box>
      )}

      <Card elevation={0} className={classes.filtersCard}>
        <Box className={classes.filtersHeader}>
          <Box display="flex" alignItems="center" gridGap={8}>
            <FilterListIcon style={{ fontSize: 18, color: "#1769d6" }} />
            <Typography style={{ fontWeight: 700, color: "#0f2d4d", fontSize: "0.9rem" }}>
              Filters
            </Typography>
          </Box>
          <Button size="small" className={classes.resetBtn} onClick={handleResetFilters}>
            Reset
          </Button>
        </Box>
        <Box className={classes.filtersBody} display="flex" flexWrap="wrap" gridGap={16}>
          <FormControl
            variant="outlined"
            size="small"
            style={{ minWidth: 280, flex: "1 1 260px" }}
            disabled={isSchoole || schoolsLoading}
          >
            <Typography className={classes.filterLabel}>
              <SchoolIcon style={{ fontSize: 16, color: "#1769d6" }} /> School
            </Typography>
            <Select
              value={filterSchoolId}
              onChange={(e) => {
                setFilterSchoolId(e.target.value);
                setFilterClassId("");
              }}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return <em>All Schools</em>;
                }
                const match = schoolList.find((s) => normalizeId(s._id) === String(selected));
                return match ? resolveSchoolLabel(match) : String(selected);
              }}
            >
              {isSuperAdmin && (
                <MenuItem value="">
                  <em>All Schools</em>
                </MenuItem>
              )}
              {schoolsLoading && (
                <MenuItem disabled value="__loading__">
                  <CircularProgress size={18} style={{ marginRight: 8 }} /> Loading schools…
                </MenuItem>
              )}
              {!schoolsLoading && schoolList.length === 0 && (
                <MenuItem disabled value="__empty__">
                  No schools found
                </MenuItem>
              )}
              {schoolList.map((school) => {
                const id = normalizeId(school._id);
                return (
                  <MenuItem key={id} value={id}>
                    {resolveSchoolLabel(school)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <FormControl variant="outlined" size="small" style={{ minWidth: 280, flex: "1 1 260px" }}>
            <Typography className={classes.filterLabel}>
              <ClassIcon style={{ fontSize: 16, color: "#1769d6" }} /> Class
            </Typography>
            <Select
              value={filterClassId}
              onChange={(e) => setFilterClassId(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">
                <em>All Classes</em>
              </MenuItem>
              {classList.map((cls) => (
                <MenuItem key={cls._id} value={cls._id}>
                  {resolveClassLabel(cls, showSchoolOnClassOptions)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Card>

      <Card elevation={0} className={classes.tabsCard}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          className={classes.tabsRoot}
        >
          {ACTIVITY_CATALOG_TABS.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
      </Card>

      <Box className={classes.tabPanel}>
        {tab === 0 && (
          <SuperActivity embedded scope={scope} sharedClassList={classList} />
        )}
        {tab === 1 && (
          <ActivityTabel embedded scope={scope} sharedClassList={classList} />
        )}
        {tab === 2 && (
          <SubActivity embedded scope={scope} sharedClassList={classList} />
        )}
      </Box>
    </Box>
  );
}
